import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../Tree.module.css";
import BSTEngine from "../TreeEngine/BSTEngine";

const BSTWrapper: React.FC = () => {
  const navigate = useNavigate();
  const [insertValue, setInsertValue] = useState<number | null>(null);
  const [deleteValue, setDeleteValue] = useState<number | null>(null);
  const [inputValue, setInputValue] = useState<string>("");
  const [deleteInputValue, setDeleteInputValue] = useState<string>("");
  const [traversal, setTraversal] = useState<string | null>(null);
  const [resetTrigger, setResetTrigger] = useState(false);
  const [highlightedNodes, setHighlightedNodes] = useState<number[]>([]);
  const [highlightLine, setHighlightLine] = useState<number | null>(null);
  const [traversalComplete, setTraversalComplete] = useState(false);
  const [generateTrigger, setGenerateTrigger] = useState<number>(0);

  const handleInsert = () => {
    if (inputValue.trim() === "") return;
    const num = Number(inputValue);
    if (isNaN(num)) return;
    setInsertValue(num);
    setInputValue("");
    setTraversal(null);
    setResetTrigger(false);
  };

  const handleDelete = () => {
    if (deleteInputValue.trim() === "") return;
    const num = Number(deleteInputValue);
    if (isNaN(num)) return;
    setDeleteValue(num);
    setDeleteInputValue("");
    setTraversal(null);
    setResetTrigger(false);
  };

  const handleTraversal = (type: string) => {
    setTraversal(null);
    setHighlightedNodes([]);
    setTraversalComplete(false);
    setResetTrigger(true);

    setTimeout(() => {
      setTraversal(type);
      setResetTrigger(false);
    }, 200);
  };

  const handleResetAnimation = () => {
    setResetTrigger(true);
    setTraversalComplete(false);
    setTimeout(() => setResetTrigger(false), 100);
  };

const handleGenerate = () => {
  setGenerateTrigger(prev => prev + 1);
  setInsertValue(null);
  setDeleteValue(null);
  setTraversal(null);
  setResetTrigger(false);
};

 return (
    <div className={styles.treeContainer}>
      <button onClick={() => navigate(-1)} className={styles.returnButton}>
        ← Return
      </button>
      <h1 className={styles.title}>Binary Search Tree (BST)</h1>

      <div className={styles.description}>
        <h2>Description</h2>
        <p>
          A <strong>Binary Search Tree (BST)</strong> is a binary tree where each node’s left child 
          contains values smaller than the parent, and the right child contains values larger. 
          This property enables efficient <strong>search</strong>, <strong>insert</strong>, and <strong>delete</strong> operations.
        </p>
        <p>
          <strong>Insert:</strong> Start at the root and compare the new value to each node. 
          If it’s smaller, move left; if larger, move right—until an empty spot is found for insertion.
          <br />
          <strong>Delete:</strong> If the node has no children, remove it. 
          If it has one child, replace it with its child. 
          If it has two children, replace it with its <em>inorder successor</em> (the smallest node in the right subtree).
        </p>

      </div>

      <div className={styles.useCase}>
        <h2>Use Cases</h2>
        <ul>
          <li>Quickly lookup or insert sorted data.</li>
          <li>Build efficient searching algorithms (O(log n)).</li>
          <li>Foundation for advanced trees like AVL and Red-Black trees.</li>
        </ul>
      </div>

      <div className={styles.complexity}>
        <h2>Time Complexity</h2>
        <ul>
          <li>Insertion: O(h)</li>
          <li>Deletion: O(h)</li>
          <li>Search: O(h)</li>
          <li>Traversal: O(n)</li>
        </ul>
        <p>
          <i>
            (note: BSTs work best when balanced — max height of 5)
          </i>
        </p>
      </div>

      <div className={styles.controls}>
        <input
          type="number"
          placeholder="Insert value"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
        />
        <button onClick={handleInsert}>Insert</button>
        <input
          type="number"
          placeholder="Delete value"
          value={deleteInputValue}
          onChange={(e) => setDeleteInputValue(e.target.value)}
        />
        <button onClick={handleDelete}>Delete</button>
        <button onClick={handleGenerate}>Generate Tree</button>
        <button onClick={() => handleTraversal("preorder")}>Preorder</button>
        <button onClick={() => handleTraversal("inorder")}>Inorder</button>
        <button onClick={() => handleTraversal("postorder")}>Postorder</button>
        <button onClick={() => handleTraversal("levelorder")}>Level Order</button>
        <button onClick={handleResetAnimation}>Reset</button>
      </div>

      <div className={styles.vizContainer}>
        <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "0.1rem" }}>
          <BSTEngine
            generateTrigger={generateTrigger}
            traversal={traversal}
            insertValue={insertValue}
            deleteValue={deleteValue}
            resetTrigger={resetTrigger}
            setTraversalComplete={setTraversalComplete}
            setHighlightedNodes={setHighlightedNodes}
            highlightedNodes={highlightedNodes}
            setHighlightLine={setHighlightLine}
          />
          {traversal && (
            <div className={styles.resultBox}>
              <h3>Traversal Result ({traversal}):</h3>
              <p>
                {highlightedNodes.length === 0
                  ? ""
                  : `Result: ${highlightedNodes.join(" → ")}`}
              </p>
            </div>
          )}  
        </div>
      </div>

      <div className={styles.pseudoContainer}>
        <h3>Pseudocode</h3>

        {traversal === "preorder" && (
          <pre className={styles.pseudo}>
            <code>
              <span className={highlightLine === 0 ? styles.highlightCode : ""}>function preorder(node):</span>{"\n"}
              <span className={highlightLine === 1 ? styles.highlightCode : ""}>  if node is null: return</span>{"\n"}
              <span className={highlightLine === 2 ? styles.highlightCode : ""}>  visit(node)</span>{"\n"}
              <span className={highlightLine === 3 ? styles.highlightCode : ""}>  preorder(node.left)</span>{"\n"}
              <span className={highlightLine === 4 ? styles.highlightCode : ""}>  preorder(node.right)</span>
            </code>
          </pre>
        )}

        {traversal === "inorder" && (
          <pre className={styles.pseudo}>
            <code>
              <span className={highlightLine === 0 ? styles.highlightCode : ""}>function inorder(node):</span>{"\n"}
              <span className={highlightLine === 1 ? styles.highlightCode : ""}>  if node is null: return</span>{"\n"}
              <span className={highlightLine === 2 ? styles.highlightCode : ""}>  inorder(node.left)</span>{"\n"}
              <span className={highlightLine === 3 ? styles.highlightCode : ""}>  visit(node)</span>{"\n"}
              <span className={highlightLine === 4 ? styles.highlightCode : ""}>  inorder(node.right)</span>
            </code>
          </pre>
        )}

        {traversal === "postorder" && (
          <pre className={styles.pseudo}>
            <code>
              <span className={highlightLine === 0 ? styles.highlightCode : ""}>function postorder(node):</span>{"\n"}
              <span className={highlightLine === 1 ? styles.highlightCode : ""}>  if node is null: return</span>{"\n"}
              <span className={highlightLine === 2 ? styles.highlightCode : ""}>  postorder(node.left)</span>{"\n"}
              <span className={highlightLine === 3 ? styles.highlightCode : ""}>  postorder(node.right)</span>{"\n"}
              <span className={highlightLine === 4 ? styles.highlightCode : ""}>  visit(node)</span>
            </code>
          </pre>
        )}

        {traversal === "levelorder" && (
          <pre className={styles.pseudo}>
            <code>
              <span className={highlightLine === 0 ? styles.highlightCode : ""}>function levelOrder(root):</span>{"\n"}
              <span className={highlightLine === 1 ? styles.highlightCode : ""}>  if root is null: return</span>{"\n"}
              <span className={highlightLine === 2 ? styles.highlightCode : ""}>  queue = [root]</span>{"\n"}
              <span className={highlightLine === 3 ? styles.highlightCode : ""}>  while queue not empty:</span>{"\n"}
              <span className={highlightLine === 4 ? styles.highlightCode : ""}>    node = queue.dequeue()</span>{"\n"}
              <span className={highlightLine === 5 ? styles.highlightCode : ""}>    visit(node)</span>{"\n"}
              <span className={highlightLine === 6 ? styles.highlightCode : ""}>    if node.left: queue.enqueue(node.left)</span>{"\n"}
              <span className={highlightLine === 7 ? styles.highlightCode : ""}>    if node.right: queue.enqueue(node.right)</span>
            </code>
          </pre>
        )}
      </div>
    </div>
  );
};

export default BSTWrapper;
